import { getTemporaryLayout } from './utils';
import { useContext, useEffect } from 'react';
import { useRouter } from 'next/router';
import { MediaQueryContext, UserContext } from 'libs/contexts';
import { amI, canI, isCurrentUserType, switchApp, USER_TYPE } from 'libs/user';
import { changeApp } from 'api/user';
import { getHref } from 'libs/urls';
import { isInSltApp } from 'libs/utils';

type MainLayoutProps = {
  layoutProps: Object;
  children: React.ReactNode;
  getLayout: Function;
};

/**
 * @description: 兼容已开发的不规范组件和后续开发的规范组件
 * @param {object} getLayout 页面组件代码中定义的getLayout，用于获取自定义布局组件
 * @param {object} layoutProps 如果页面组件有这个属性就使用getTemporaryLayout获取布局组件
 * @returns
 */

// 路由白名单：满足条件时不进行 changeApp 后的跳转
const NO_REDIRECT_PATHS = [
  '/logistics-map',
  '/meeting',
  '/pages/welfare',
  '/message-news/detail/[id]',
];
const isNoRedirectPath = (pathname: string): boolean => {
  return NO_REDIRECT_PATHS.includes(pathname);
};

export default function MainLayout({ getLayout, layoutProps, children }: MainLayoutProps) {
  const { isMobile } = useContext(MediaQueryContext);
  const userInfo = useContext(UserContext);
  const router = useRouter();

  /**
   * 移动端角色切换逻辑
   * 功能：运营人员在移动端自动切换到采购商/供应商角色
   * 原位置：_app.tsx line 292-316
   */
  useEffect(() => {
    if (!isMobile || !userInfo || !amI(userInfo, 'operation')) return;

    const myApp = userInfo?.applications ?? [];
    if (canI(userInfo, 'purchaser')) {
      const { sysNo } = myApp.find((a) => a.key === 'purchaser');
      changeApp(sysNo).then(() => {
        if (isNoRedirectPath(router.pathname)) {
          return;
        } else {
          // window.location.href = getHref('/mine');
        }
      });
    } else if (canI(userInfo, 'supplier')) {
      const { sysNo } = myApp.find((a) => a.key === 'supplier');
      changeApp(sysNo).then(() => {
        if (isNoRedirectPath(router.pathname)) {
          return;
        } else {
          // window.location.href = getHref('/mine');
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userInfo, isMobile, router.pathname]);

  /**
   * 商链通 App 角色切换逻辑
   * 功能：商链通 App 中自动切换到供应商角色
   * 原位置：_app.tsx line 320-335
   */
  useEffect(() => {
    if (!userInfo) return;
    if (!isInSltApp(window.navigator.userAgent)) return;
    if (isCurrentUserType(userInfo, USER_TYPE.SUPPLIER)) return;

    const flag = 'slt-switch-done';
    if (sessionStorage.getItem(flag)) {
      sessionStorage.removeItem(flag);
      return;
    }

    switchApp(userInfo, 'supplier', () => {
      sessionStorage.setItem(flag, '1');
      window.location.reload();
    });
  }, [userInfo]);

  /**
   * 分供方门户对接逻辑
   * 功能：分供方门户的 SSO 登录对接
   * 原位置：_app.tsx line 221-233
   */
  useEffect(() => {
    const urlSearchStr = window.location.search;
    if (urlSearchStr.includes('refer=supplierPortal')) {
      console.log('确定是分供方进来的');
      if (!!userInfo && urlSearchStr.includes('authTicket=')) {
        // 交给后台登录，并重定向会原地址
        let destUrl = `/sso/login?return_url=${encodeURIComponent(window.location.href)}`;
        const resultUrl = `${window.location.origin}/${destUrl}`;
        console.log('result url', resultUrl);
        window.location.href = resultUrl;
      }
    }
  }, [userInfo]);

  let resultPage: JSX.Element;
  if (layoutProps) {
    resultPage = getTemporaryLayout(children, layoutProps, isMobile);
  } else {
    resultPage = getLayout(children);
  }

  return resultPage;
}
